<template>
  <DataTable
    :value="brands"
    :class="[
      brands && brands.length == 0 ? 'empty-table' : '',
      isActiveFirst ? 'active-first' : '',
    ]"
    class="scroll-table-y main-table scrollbar-custom"
    stripedRows
    @sort="sortTable"
    removableSort
    scrollable
    :loading="isLoading"
    dataKey="id"
    columnResizeMode="expand"
  >
    <Column field="number" header="№" style="min-width: 44px">
      <template #body="slotProps">
        {{ rowsCount + slotProps.index + 1 }}
      </template>
    </Column>
    <Column field="brand_name" header="Название бренда" sortable>
      <template #body="slotProps">
        <RouterLink
          :to="{ name: 'brand', params: { id: slotProps.data.brand_pk } }"
        >
          {{ slotProps.data.brand_name }}
        </RouterLink>
      </template>
    </Column>
    <Column field="use_in_wb_parser" header="WB Parser">
      <template #body="slotProps">
        <span v-if="slotProps.data.is_used_in_wbparser">Да</span>
        <span v-else>Нет</span>
      </template>
    </Column>
    <Column field="use_in_dl" header="DataLens">
      <template #body="slotProps">
        <span v-if="slotProps.data.is_used_in_datalens">Да</span>
        <span v-else>Нет</span>
      </template>
    </Column>
    <Column header="Категории" style="max-width: 150px">
      <template #body="slotProps">
        <ListWithTooltips
          :slotProps="slotProps.data.categories"
          items="categories"
          item-key="id"
          item-value="name"
        />
      </template>
    </Column>

    <Column header="Паттерны" style="max-width: 150px">
      <template #body="slotProps">
        <ListWithTooltips
          :slotProps="slotProps.data.brand_patterns"
          items="patterns"
          item-key="pattern"
          item-value="pattern"
        />
      </template>
    </Column>

    <Column field="cuser" header="Автор">
      <template #body="slotProps">
        {{ getUserFullName(slotProps.data.cuser) }}
      </template>
    </Column>
    <Column
      field="ctime"
      header="Дата создания"
      sortable
      style="min-width: 153px"
    >
      <template #body="slotProps">
        {{ convertDateFromUTC(slotProps.data.ctime) }}
      </template>
    </Column>
    <Column
      field="mtime"
      header="Дата обновления"
      sortable
      style="min-width: 173px; max-width: 173px"
    >
      <template #body="slotProps">
        {{ convertDateFromUTC(slotProps.data.mtime) }}
      </template>
    </Column>
    <Column frozen alignFrozen="right">
      <template #body="slotProps">
        <div
          @click="(event) => toggle(slotProps.data.brand_pk, event)"
          aria-haspopup="true"
          aria-controls="overlay_menu"
          class="button-menu"
        >
          <span :data-segment="1" class="pi pi-ellipsis-v"></span>
        </div>
      </template>
    </Column>
  </DataTable>
  <Menu ref="menu" id="overlay_menu" :model="filteredItems" :popup="true" />
  <DeleteConfirmDialog
    deleted-name="бренд"
    :visible="confirmVisible.delete"
    :blocked="blockedButtonDelete"
    @close="confirmVisible.delete = false"
    @delete="deleteBrand"
  />
  <UsersHistoryDialog
    entity="brands"
    :object_id="modifiedId"
    :visible="confirmVisible.history"
    @close="confirmVisible.history = false"
  ></UsersHistoryDialog>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/store/auth/login";
import { useBrandsStore } from "@/store/dictionaries/brands";

import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Menu from "primevue/menu";
import ListWithTooltips from "@/entities/segments/table/ListWithTooltips.vue";
import UsersHistoryDialog from "@/widgets/dialogs/UsersHistoryDialog.vue";
import DeleteConfirmDialog from "@/shared/dialogs/DeleteConfirmDialog.vue";

import { convertDateFromUTC } from "@/services/utils/DateParser";
import { getUserFullName } from "@/services/utils/Names";
import { rootGetters } from "@/main";

defineProps<{
  brands: any[];
  isActiveFirst: boolean;
  isLoading: boolean;
  rowsCount: number;
}>();
const emits = defineEmits(["sortTable", "updateTable"]);
const router = useRouter();
const store = useBrandsStore();
const authStore = useAuthStore();

const isDenied = computed(() => {
  return authStore.isDeniedForPermissions(["history:edit"], ["history:read"]);
});

let modifiedId = ref("");
const confirmVisible = ref({
  history: false,
  delete: false,
});
const blockedButtonDelete = ref(false);
const menu = ref();
const items = ref([
  {
    label: "История действий",
    icon: "pi pi-history",
    checkDenied: true,
    archive: "both",
    command: () => {
      confirmVisible.value.history = true;
    },
  },
  {
    label: "Редактировать",
    icon: "pi pi-pencil",
    archive: "no_archive",
    command: () => {
      router.push({ name: "brand", params: { id: modifiedId.value } });
    },
  },
  {
    label: "Удалить",
    icon: "pi pi-trash",
    archive: "both",
    command: () => {
      confirmVisible.value.delete = true;
    },
  },
]);

const filteredItems = computed(() => {
  return items.value.filter((item) => !(item.checkDenied && isDenied.value));
});

const toggle = (elemId: string, event: any) => {
  modifiedId.value = elemId;
  menu.value.toggle(event);
};

const sortTable = (event: any) => {
  let orderField = "";
  if (event.sortField)
    orderField = event.sortOrder > 0 ? event.sortField : `-${event.sortField}`;
  emits("sortTable", orderField);
};

const deleteBrand = () => {
  blockedButtonDelete.value = true;
  store
    .deleteBrand(modifiedId.value)
    .finally(() => {
      emits("updateTable");
      blockedButtonDelete.value = false;
      confirmVisible.value.delete = false;
    })
    .catch((err) => {
      if (err.response && err.response.status == 400)
        rootGetters.toast.show("warn", "Ошибка", err.response.data.detail);
    });
};
</script>

<style lang="scss" scoped>
.table-chip {
  background-color: #eaeaf0;
  padding: 4px;
  border-radius: 4px;
  font-size: 1rem;
  line-height: 13px;
}
</style>
